import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, FileDown, Loader2, Lock, Pencil, RefreshCcw, Send, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/app/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useApproveRescuePlan,
  useDeleteRescuePlan,
  useRescuePlan,
  useRevertRescuePlanToDraft,
  useReturnRescuePlanForChanges,
  useSubmitRescuePlanForReview,
} from '@/hooks/use-safety';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { toast } from '@/app/components/ui/simple-toaster';
import { isAxiosError } from 'axios';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { RescuePlanStatus } from '@/lib/types';
import { SafetyDiagramsSection } from '../safety-diagrams-section';
import { CrewInitialsCard } from '@/app/components/safety/crew-initials-card';
import {
  ReturnForChangesDialog,
  RevisionWarningDialog,
} from '@/app/components/safety/plan-action-dialogs';

const statusColors: Record<RescuePlanStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  signed_off: 'bg-indigo-100 text-indigo-800',
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  active: 'bg-blue-100 text-blue-800',
  closed: 'bg-gray-100 text-gray-600',
};

export function RescuePlanDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const planId = parseInt(id || '0', 10);
  const { data: plan, isLoading } = useRescuePlan(planId);
  const approveMutation = useApproveRescuePlan();
  const deleteMutation = useDeleteRescuePlan();
  const submitForReviewMutation = useSubmitRescuePlanForReview();
  const revertToDraftMutation = useRevertRescuePlanToDraft();
  const returnMutation = useReturnRescuePlanForChanges();
  const { isAdmin, hasMinModuleRole } = usePermissions();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [actionErrors, setActionErrors] = useState<Record<string, string[]> | null>(null);
  const [showRevisionWarning, setShowRevisionWarning] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const canManageSafety = hasMinModuleRole('safety', 'manager');

  const parseFieldErrors = (error: unknown): Record<string, string[]> | null => {
    if (isAxiosError(error) && error.response?.data && typeof error.response.data === 'object') {
      const data = error.response.data as Record<string, unknown>;
      if (!('error' in data)) {
        const errors: Record<string, string[]> = {};
        for (const [key, value] of Object.entries(data)) {
          if (Array.isArray(value)) {
            errors[key] = value.filter((v): v is string => typeof v === 'string');
          } else if (typeof value === 'string') {
            errors[key] = [value];
          }
        }
        if (Object.keys(errors).length > 0) {
          return errors;
        }
      }
    }
    return null;
  };

  const handleSubmitForReview = () => {
    setActionErrors(null);
    submitForReviewMutation.mutate(planId, {
      onSuccess: (updatedPlan) => {
        toast.success(updatedPlan.review_route.message);
      },
      onError: (error) => {
        if (
          isAxiosError<{ error?: string }>(error) &&
          error.response?.data?.error
        ) {
          toast.error(error.response.data.error);
          return;
        }
        const fieldErrors = parseFieldErrors(error);
        if (fieldErrors) {
          setActionErrors(fieldErrors);
        } else {
          toast.error('Failed to submit rescue plan for review.');
        }
      },
    });
  };

  const handleApprove = () => {
    setActionErrors(null);
    approveMutation.mutate(planId, {
      onSuccess: () => {
        toast.success('Rescue plan approved.');
      },
      onError: (error) => {
        if (
          isAxiosError<{ error?: string }>(error) &&
          error.response?.data?.error
        ) {
          toast.error(error.response.data.error);
          return;
        }
        const fieldErrors = parseFieldErrors(error);
        if (fieldErrors) {
          setActionErrors(fieldErrors);
        } else {
          toast.error('Failed to approve rescue plan.');
        }
      },
    });
  };

  const handleRevertToDraft = () => {
    setActionErrors(null);
    revertToDraftMutation.mutate(planId, {
      onSuccess: (revision) => {
        toast.success(
          revision.id === planId
            ? 'Rescue plan reverted to draft.'
            : 'New rescue plan revision created.'
        );
        if (revision.id !== planId) {
          navigate(`/safety/rescue-plans/${revision.id}/edit`);
        }
      },
      onError: (error) => {
        const message =
          isAxiosError<{ error?: string }>(error) && error.response?.data?.error
            ? error.response.data.error
            : 'Failed to revert rescue plan to draft.';
        toast.error(message);
      },
    });
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this draft rescue plan?')) return;
    deleteMutation.mutate(planId, {
      onSuccess: () => navigate('/safety/rescue-plans'),
      onError: (error) =>
        toast.error(
          isAxiosError<{ detail?: string }>(error) && error.response?.data?.detail
            ? error.response.data.detail
            : 'Failed to delete rescue plan.',
        ),
    });
  };

  const handleDownloadPdf = async () => {
    if (!plan) return;
    setDownloading(true);
    try {
      const response = await apiClient.get(`/api/v1/safety/rescue-plans/${plan.id}/pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          `RescuePlan-${plan.id}.pdf`,
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Rescue plan not found.</p>
        <Button asChild className="mt-4">
          <Link to="/safety/rescue-plans">Back to Rescue Plans</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/rescue-plans')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{plan.title}</h1>
            <p className="text-gray-600">
              {plan.location} &middot; {formatDateOnly(plan.date)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={plan.is_superseded ? 'bg-gray-100 text-gray-600' : statusColors[plan.status]}>
            {plan.is_superseded ? 'Superseded' : plan.status.replace('_', ' ')}
          </Badge>
          {plan.self_approved && <Badge variant="outline">Self-approved</Badge>}
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloading}>
            {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
            PDF
          </Button>
        </div>
      </div>

      {plan.is_locked && (
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertDescription>
            This rescue plan is locked{plan.status === 'signed_off' ? ' (signed off)' : plan.status === 'approved' ? ' (approved)' : ''} and cannot be edited.
            {plan.signed_off_by_username && plan.signed_off_at && (
              <span className="ml-1 text-gray-500">
                Signed off by {plan.signed_off_by_username} on{' '}
                {formatDateTimeInTimeZone(plan.signed_off_at, timeZone)}
              </span>
            )}
            {plan.reviewed_by_username && plan.reviewed_at && (
              <span className="ml-1 text-gray-500">
                Approved by {plan.reviewed_by_username} on{' '}
                {formatDateTimeInTimeZone(plan.reviewed_at, timeZone)}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {plan.status === 'pending_review' && (
        <Alert>
          <Send className="h-4 w-4" />
          <AlertTitle>Awaiting approval</AlertTitle>
          <AlertDescription>{plan.review_route.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Plan Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Project:</span> {plan.project_name || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Created By:</span> {plan.created_by_username}
            </div>
            {plan.jsa_title && (
              <div>
                <span className="text-gray-500">Linked JSA:</span>{' '}
                <Link to={`/safety/jsas/${plan.jsa}`} className="text-indigo-600 hover:underline">
                  {plan.jsa_title}
                </Link>
              </div>
            )}
            {plan.safe_work_plan_title && (
              <div>
                <span className="text-gray-500">Linked Work Plan:</span>{' '}
                {plan.safe_work_plan_title}
              </div>
            )}
            {plan.estimated_rescue_time_minutes && (
              <div>
                <span className="text-gray-500">Est. Rescue Time:</span>{' '}
                {plan.estimated_rescue_time_minutes} min
              </div>
            )}
          </div>
          {plan.rescue_team_description && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Rescue Team</p>
              <p className="text-sm whitespace-pre-wrap">{plan.rescue_team_description}</p>
            </div>
          )}
          {plan.communication_protocol && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Communication Protocol</p>
              <p className="text-sm whitespace-pre-wrap">{plan.communication_protocol}</p>
            </div>
          )}
          {plan.rescue_equipment_location && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Rescue Equipment Location</p>
              <p className="text-sm whitespace-pre-wrap">{plan.rescue_equipment_location}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {plan.steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Rescue Steps</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Est. Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.steps.map((step) => (
                  <TableRow key={step.id}>
                    <TableCell className="font-medium">{step.step_number}</TableCell>
                    <TableCell>{step.description}</TableCell>
                    <TableCell className="text-gray-600">
                      {step.responsible_role || '-'}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {step.estimated_time_minutes ? `${step.estimated_time_minutes} min` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {plan.equipment.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Rescue Equipment</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Staging Location</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.equipment.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.equipment_name}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell className="text-gray-600">{item.location_staged}</TableCell>
                    <TableCell className="text-gray-600">{item.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Emergency Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Nearest Hospital:</span>{' '}
              {plan.nearest_hospital || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Hospital Phone:</span>{' '}
              {plan.hospital_phone || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Emergency Services:</span>{' '}
              {plan.emergency_services_phone}
            </div>
          </div>
          {plan.hospital_route_description && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Hospital Route</p>
              <p className="text-sm whitespace-pre-wrap">{plan.hospital_route_description}</p>
            </div>
          )}
          {plan.site_access_notes && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Site Access Notes</p>
              <p className="text-sm whitespace-pre-wrap">{plan.site_access_notes}</p>
            </div>
          )}
          {plan.additional_hazards && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Additional Hazards</p>
              <p className="text-sm whitespace-pre-wrap">{plan.additional_hazards}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {plan.signatures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Signatures ({plan.signatures.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Signed At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.signatures.map((sig) => (
                  <TableRow key={sig.id}>
                    <TableCell className="font-medium">{sig.signer_name}</TableCell>
                    <TableCell>{sig.signer_role}</TableCell>
                    <TableCell>{formatDateTimeInTimeZone(sig.signed_at, timeZone)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CrewInitialsCard summary={plan.crew_initials} timeZone={timeZone} canInitial={['approved', 'active', 'signed_off'].includes(plan.status)} />

      <SafetyDiagramsSection contentType="rescueplan" objectId={plan.id} canEdit={plan.status === 'draft'} />

      {actionErrors && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Action blocked</AlertTitle>
          <AlertDescription>
            <p className="font-medium">The rescue plan is missing required content:</p>
            <ul className="list-disc ml-5 mt-1 space-y-1">
              {Object.entries(actionErrors).flatMap(([field, messages]) =>
                messages.map((message, idx) => (
                  <li key={`${field}-${idx}`}>{field.replace(/_/g, ' ')}: {message}</li>
                ))
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-3">
        {plan.status === 'draft' && (isAdmin || user?.id === plan.created_by) && (
          <>
            <Button variant="outline" asChild>
              <Link to={`/safety/rescue-plans/${plan.id}/edit`}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Link>
            </Button>
            {!plan.ever_submitted && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </>
        )}
        {(plan.status === 'approved' || plan.status === 'signed_off') &&
          (plan.project === null || plan.is_current) &&
          canManageSafety && (
          <Button
            variant="outline"
            onClick={() => {
              if (plan.project === null) {
                handleRevertToDraft();
              } else {
                setShowRevisionWarning(true);
              }
            }}
            disabled={revertToDraftMutation.isPending}
          >
            {revertToDraftMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4 mr-1" />
            )}
            {revertToDraftMutation.isPending
              ? plan.project === null
                ? 'Reverting...'
                : 'Creating...'
              : plan.project === null
                ? 'Revert to Draft'
                : 'Create New Revision'}
          </Button>
        )}
        {plan.status === 'draft' && canManageSafety && (
          <Button
            onClick={handleSubmitForReview}
            disabled={submitForReviewMutation.isPending}
          >
            {submitForReviewMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1" />
            )}
            {submitForReviewMutation.isPending ? 'Submitting...' : 'Submit for Review'}
          </Button>
        )}
        {plan.status === 'pending_review' && plan.can_approve && (
          <>
            <Button
              variant="outline"
              onClick={() => setShowReturnDialog(true)}
              disabled={returnMutation.isPending}
            >
              Return for changes
            </Button>
            <Button
              onClick={handleApprove}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve Rescue Plan'}
            </Button>
          </>
        )}
      </div>
      <RevisionWarningDialog
        open={showRevisionWarning}
        onOpenChange={setShowRevisionWarning}
        nextVersion={(plan.version ?? 1) + 1}
        onContinue={handleRevertToDraft}
      />
      <ReturnForChangesDialog
        open={showReturnDialog}
        onOpenChange={setShowReturnDialog}
        isPending={returnMutation.isPending}
        onSubmit={(reason) =>
          returnMutation.mutate(
            { id: plan.id, reason },
            {
              onSuccess: () => {
                setShowReturnDialog(false);
                toast.success('Rescue plan returned for changes.');
              },
              onError: () => toast.error('Failed to return rescue plan for changes.'),
            },
          )
        }
      />
    </div>
  );
}
